import React, { Component, PropTypes } from 'react';
import { Link } from 'react-router';
import { SubmissionError } from 'redux-form';
import { createUserRequest, createRegistrationWaiterRequest } from 'common/ducks/user';
import Step1Form from 'registration/common/components/step1_form';
import CreativeIcon from 'assets/images/icons/icon_creative.svg';
import ClientIcon from 'assets/images/icons/icon_client.svg';
import { Meta } from 'common/components/meta';
import { getCanonicalURL } from 'common/utils';
import { trackGoogle, setGoogleDimension } from 'common/tracking';
import {
  trackMixpanel, aliasWithMixpanel, identifyWithMixpanel,
  setMixpanelProfile, mixpanelProfileFromCsProfile, MP_VIEWED_MARKETING_PAGE_EVENT_NAME
} from 'common/tracking';
import { CREATIVE_REGISTRATION_CLOSED } from 'registration/constants';

/**
 * First step of client and creative registration.
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
class Step1 extends Component {
  constructor(props) {
    super(props)
    this.state = {isClient: false, done: false}
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  componentDidMount() {
    trackGoogle("registration", "started registration", "regular");
    trackMixpanel(
      MP_VIEWED_MARKETING_PAGE_EVENT_NAME,
      {
        "pageName": "Join"
      }
    )
    trackMixpanel("Started registration", {"variant": "regular"});
  }

  createUser(formData) {
    const { isClient } = this.state;
    const { updateUserLocal, setAgencySegment } = this.props;

    formData = Object.assign({ is_client: isClient, is_creative: !isClient }, formData);

    return createUserRequest(formData)
      .then(({ data }) => {
        if (data.user_converted_from) {
          // dimension1 = user_converted_from (defined in analytics)
          setGoogleDimension('dimension1', data.user_converted_from);
        }
        aliasWithMixpanel(data.id);
        identifyWithMixpanel(data.id);
        setMixpanelProfile(data.email, mixpanelProfileFromCsProfile(data), false);

        trackGoogle("registration", "registered", isClient ? "client" : "creative");
        trackMixpanel("Registered", {
          "variant": "regular",
          "userType": isClient ? "client" : "creative"
        });
        updateUserLocal(data);
        this.setState({ done: true });

        if (isClient) {
          setAgencySegment(formData.is_agency === "yes");
        }
      })
      .catch((res) => {
        /* field-specific errors */
        if (res.data) {
          if (res.data.username) throw new SubmissionError({username: res.data.username[0]})
          if (res.data.email) throw new SubmissionError({email: res.data.email[0]})
          if (res.data.password) throw new SubmissionError({password: res.data.password[0]})
        }

        /* global error */
        throw new SubmissionError({_error: 'Unknown error. Please try again.'})
      })
  }

  handleSubmit(formData) {
    const { isClient } = this.state;
    const { creativeRegistrationClosed, inviteHash, redeemInvite, inviteRedeemed } = this.props;

    /* user coming from special invite link to creative registration */
    if (!isClient && inviteHash && !inviteRedeemed && CREATIVE_REGISTRATION_CLOSED) {
      return redeemInvite(inviteHash).catch((res) => {
        throw new SubmissionError({ _error: 'Invitation already redeemed' });
      }).then(() => {
        return this.createUser(formData);
      });
    }

    if (!isClient && creativeRegistrationClosed) {
      return createRegistrationWaiterRequest(formData).then(() => {
        this.setState({ done: true });
      }).catch((res) => {
        /* field-specific errors */
        if (res.data) {
          if (res.data.email) throw new SubmissionError({ email: res.data.email[0] })
        }

        /* global error */
        throw new SubmissionError({_error: 'Unknown error. Please try again.'})
      });
    }

    return this.createUser(formData);
  }

  renderContent() {
    const { isClient, done } = this.state;
    const { user, creativeRegistrationClosed } = this.props;

    /* only anonymous users should be able to register */
    if (user.logged_in && !done) {
      return (
        <div className="done">
          <h3>You are already registered.</h3>
          <p>Click on the button below to go to Your Projects.</p>
          <Link to='/your-projects/' className="btn btn-tertiary">Your Projects</Link>
        </div>
      )
    }

    /* step completed */
    if (done) {
      if (!isClient && creativeRegistrationClosed) {
        return (
          <div className="done">
            <h3 className="text-success">
              <i className="fa fa-check-circle"></i> We added you to our wait list.
            </h3>
            <p>
              Thanks for submitting your email.<br/>
              We will email you when our next open registration period starts.
            </p>
            <p>
              Please DO NOT contact our support team to ask when you can register - they will not
              be able to help you until we re-open registration.
            </p>
            <p>
              Please visit our <Link target="_blank" to="/help/joining-and-verification/why-does-crowdspring-have-wait-list-register-designer-or-namer/"> Wait List FAQ</Link> to learn more.
            </p>
          </div>
        );
      }

      return (
        <div className="done">
          <h3 className="text-success"><i className="fa fa-check-circle"></i> Verification Sent</h3>
          <p>We sent a verification to the email you provided.<br/> Please check your inbox and click on the link in the email to complete your registration.</p>
        </div>
      )
    }

    return (
      <div className="register-content">
        <h3>Join as a&hellip;</h3>

        <ul className="row">
          <li className="columns small-8" onClick={() => this.setState({isClient: false})}>
            <div className={"tile " + (!isClient? " selected":"")}>
              <i className="icon" dangerouslySetInnerHTML={{__html: CreativeIcon}}/>
              <h4>Creative</h4>
            </div>
          </li>
          <li className="columns small-8" onClick={() => this.setState({isClient: true})}>
            <div className={"tile " + (isClient? " selected":"")}>
              <i className="icon" dangerouslySetInnerHTML={{__html: ClientIcon}}/>
              <h4>Client</h4>
            </div>
          </li>
        </ul>

        <Step1Form onSubmit={this.handleSubmit} isClient={isClient}
          creativeRegistrationClosed={creativeRegistrationClosed} />
      </div>)

  }

  render() {
    return (
      <div id="content" className="l-content registration common step1">
        <Meta
          title="Registration"
          meta={[
            { name: "robots", content: "noindex,follow" },
          ]}
          link={[{ rel: "canonical", href: getCanonicalURL("/register/" ) }]}
        />
        <div className="row align-center">
          <div className="columns small-16 medium-10 large-6">
            { this.renderContent() }
          </div>
        </div>
      </div>
    )
  }
}

Step1.propTypes = {
  user: PropTypes.object.isRequired,
  updateUserLocal: PropTypes.func.isRequired,
  creativeRegistrationClosed: PropTypes.bool.isRequired,
  redeemInvite: PropTypes.func.isRequired,
  inviteHash: PropTypes.string,
  inviteRedeemed: PropTypes.bool,
}

export default Step1;



// WEBPACK FOOTER //
// ./registration/common/components/step1.jsx